/**
 * SuggestionsDropdown - Dropdown menu for quick access to suggestions in idle state.
 *
 * Shows available protocols (pinned) and system suggestions with dynamic icon:
 * - SlashSquare if only protocols available
 * - Sparkles if suggestions available (or both)
 *
 * Displays a count label next to the icon with tooltip explaining what's available.
 */

import {
  getCategoryIcon,
  type SuggestedChip,
} from '@/components/ui/transcript-chips/types';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { SlashSquare, Sparkles } from 'lucide-react';
import { memo, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { type PinnedChip } from './evidence-settings-popover';

export interface SuggestionsDropdownProps {
  /** Pinned/saved protocol chips */
  pinnedChips: PinnedChip[];
  /** System-generated suggestion chips */
  visibleChips: SuggestedChip[];
  /** Handler for pinned chip click */
  onPinnedChipClick?: (chip: PinnedChip) => void;
  /** Handler for suggestion chip click */
  onChipClick?: (chip: SuggestedChip) => void;
  /** Whether the dropdown trigger is disabled */
  disabled?: boolean;
}

export const SuggestionsDropdown = memo(
  ({
    pinnedChips,
    visibleChips,
    onPinnedChipClick,
    onChipClick,
    disabled = false,
  }: SuggestionsDropdownProps) => {
    const intl = useIntl();
    const hasPinnedChips = pinnedChips.length > 0;
    const hasSystemChips = visibleChips.length > 0;
    const totalCount = pinnedChips.length + visibleChips.length;

    // Dynamic icon: SlashSquare if only pinned, Sparkles otherwise
    const SuggestionsIcon = useMemo(() => {
      if (hasPinnedChips && !hasSystemChips) return SlashSquare;
      return Sparkles;
    }, [hasPinnedChips, hasSystemChips]);

    const tooltipText = useMemo(() => {
      const scenario =
        hasPinnedChips && hasSystemChips
          ? 'both'
          : hasPinnedChips
            ? 'pinnedOnly'
            : hasSystemChips
              ? 'suggestionsOnly'
              : 'other';

      return intl.formatMessage(
        {
          defaultMessage: `{scenario, select, both {{pinnedCount, plural, one {# shortcut} other {# shortcuts}} and {suggestionCount, plural, one {# suggestion} other {# suggestions}} available} pinnedOnly {{pinnedCount, plural, one {# shortcut} other {# shortcuts}} available} suggestionsOnly {{suggestionCount, plural, one {# suggestion} other {# suggestions}} available} other {No shortcuts or suggestions available}}`,
          id: 'qBvJLhCIlu',
          description:
            'Tooltip on the Ask Heidi suggestions control summarizing counts of pinned shortcuts and system suggestions',
        },
        {
          scenario,
          pinnedCount: pinnedChips.length,
          suggestionCount: visibleChips.length,
        }
      );
    }, [hasPinnedChips, hasSystemChips, intl, pinnedChips.length, visibleChips.length]);

    const viewSuggestionsAriaLabel = intl.formatMessage({
      defaultMessage: 'View suggestions',
      id: 'H3QCOx2Sl2',
      description:
        'Accessibility label for opening shortcuts and suggestions from the floating bar',
    });

    return (
      <DropdownMenu>
        <Tooltip delayDuration={400}>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                aria-label={viewSuggestionsAriaLabel}
              >
                <SuggestionsIcon size={14} />
                <span className="text-xs tabular-nums text-text-secondary">
                  {totalCount}
                </span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={8}>
            {tooltipText}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" side="top" sideOffset={8} className="w-64">
          {hasPinnedChips && (
            <>
              <DropdownMenuLabel className="flex items-center gap-2">
                <SlashSquare size={14} className="text-text-secondary" />
                <FormattedMessage
                  defaultMessage="Shortcuts"
                  id="9wxcEKJsWG"
                  description="Section heading for user-pinned shortcut chips in the suggestions menu"
                />
              </DropdownMenuLabel>
              <DropdownMenuGroup>
                {pinnedChips.map((chip) => (
                  <DropdownMenuItem
                    key={chip.id}
                    onClick={() => onPinnedChipClick?.(chip)}
                    className="cursor-pointer"
                  >
                    <SlashSquare size={14} className="text-text-secondary" />
                    <span className="truncate">{chip.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </>
          )}
          {hasPinnedChips && hasSystemChips && <DropdownMenuSeparator />}
          {hasSystemChips && (
            <>
              <DropdownMenuLabel className="flex items-center gap-2">
                <Sparkles size={14} className="text-text-secondary" />
                <FormattedMessage
                  defaultMessage="Suggestions"
                  id="lgwy0nOIG0"
                  description="Section heading for system-generated suggestion chips in the suggestions menu"
                />
              </DropdownMenuLabel>
              <DropdownMenuGroup>
                {visibleChips.map((chip) => {
                  const CategoryIcon = getCategoryIcon(chip.category);
                  return (
                    <DropdownMenuItem
                      key={chip.id}
                      onClick={() => onChipClick?.(chip)}
                      className="cursor-pointer"
                    >
                      <CategoryIcon size={14} className="text-text-secondary" />
                      <span className="truncate">{chip.label}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);

SuggestionsDropdown.displayName = 'SuggestionsDropdown';
